import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './contexts/AuthContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import LoadingSpinner from './components/LoadingSpinner';
import { setupLoadingInterceptor } from './services/api';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MemberList from './pages/MemberList';
import MemberForm from './pages/MemberForm';
import MemberDetail from './pages/MemberDetail';
import FreezeMember from './pages/FreezeMember';
import MembershipPlans from './pages/MembershipPlans';
import MembershipPurchase from './pages/MembershipPurchase';
import MembershipList from './pages/MembershipList';
import Invoice from './pages/Invoice';
import BranchList from './pages/BranchList';
import BranchForm from './pages/BranchForm';
import BranchDetail from './pages/BranchDetail';
import TrainerList from './pages/TrainerList';
import TrainerForm from './pages/TrainerForm';
import TrainerDetail from './pages/TrainerDetail';
import Analytics from './pages/Analytics';

// Placeholder pages
const Attendance = () => <div>Attendance Page (Coming Soon)</div>;
const Payments = () => <div>Payments Page (Coming Soon)</div>;
const Classes = () => <div>Classes Page (Coming Soon)</div>;
const Equipment = () => <div>Equipment Page (Coming Soon)</div>;
const Notifications = () => <div>Notifications Page (Coming Soon)</div>;
const Settings = () => <div>Settings Page (Coming Soon)</div>;

// Component to setup loading interceptor
const LoadingInterceptorSetup = ({ children }) => {
  const { showLoading, hideLoading } = useLoading();

  useEffect(() => {
    setupLoadingInterceptor(showLoading, hideLoading);
  }, [showLoading, hideLoading]);

  return children;
};

function App() {
  return (
    <Router>
      <LoadingProvider>
        <AuthProvider>
          <LoadingInterceptorSetup>
            <div className="App">
              <LoadingSpinner />
              <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/members"
              element={
                <PrivateRoute>
                  <Layout>
                    <MemberList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/members/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <MemberForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/members/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <MemberDetail />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/members/:id/edit"
              element={
                <PrivateRoute>
                  <Layout>
                    <MemberForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/members/:id/freeze"
              element={
                <PrivateRoute>
                  <Layout>
                    <FreezeMember />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/membership/plans"
              element={
                <PrivateRoute>
                  <Layout>
                    <MembershipPlans />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/membership/purchase"
              element={
                <PrivateRoute>
                  <Layout>
                    <MembershipPurchase />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/memberships"
              element={
                <PrivateRoute>
                  <Layout>
                    <MembershipList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/invoice/:invoiceId"
              element={
                <PrivateRoute>
                  <Layout>
                    <Invoice />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Trainer Routes */}
            <Route
              path="/trainers"
              element={
                <PrivateRoute>
                  <Layout>
                    <TrainerList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/trainers/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <TrainerForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/trainers/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <TrainerDetail />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/trainers/edit/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <TrainerForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Branch Routes */}
            <Route
              path="/branches"
              element={
                <PrivateRoute>
                  <Layout>
                    <BranchList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/branches/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <BranchForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/branches/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <BranchDetail />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/branches/edit/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <BranchForm />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/attendance"
              element={
                <PrivateRoute>
                  <Layout>
                    <Attendance />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <PrivateRoute>
                  <Layout>
                    <Payments />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/classes"
              element={
                <PrivateRoute>
                  <Layout>
                    <Classes />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/equipment"
              element={
                <PrivateRoute>
                  <Layout>
                    <Equipment />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <PrivateRoute>
                  <Layout>
                    <Analytics />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <Layout>
                    <Notifications />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </PrivateRoute>
              }
            />
                <Route path="/" element={<Navigate to="/dashboard" />} />
              </Routes>
              <ToastContainer position="top-right" autoClose={3000} />
            </div>
          </LoadingInterceptorSetup>
        </AuthProvider>
      </LoadingProvider>
    </Router>
  );
}

export default App;
